/**
 * `readinessModel.ts` — WP-10 (Simulation readiness ToolCard).
 *
 * Pure helpers and taxonomy shared by:
 *
 *  - The left-rail `SimulationReadinessPanel` (PHASE-10 baseline,
 *    re-exported from `frontend/src/app/TwinProductUx.tsx` so its
 *    public surface and existing tests stay behaviorally unchanged).
 *  - The right-rail `ReadinessToolCard` (WP-10 — activated when
 *    `useViewerStore.sceneSelection.toolCardKind === 'readiness'` or
 *    when a `readiness-gap` / `ai-candidate` index node is selected).
 *
 * Both surfaces import everything they need from this module so the
 * readiness logic — domain grouping, L0..L4 chip rendering, evidence
 * resolution — exists in exactly one place.
 *
 * Spec references:
 *
 *  - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md`
 *    §7.10 Card 10 + §8.10 implementation prompt.
 *  - `docs/results/PHASE-10-SIMULATION-READINESS-COMPLETION.md`.
 *  - `geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md`
 *    "Status, severity, lifecycle" + "AI/ML" sections.
 *  - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 — every
 *    color-coded state carries a non-color cue (icon).
 */

import type {
  SimulationReadinessChecklistItem,
  SimulationReadinessReport,
  UnifiedSceneTreeNode,
} from "@/app/scenePackage";
import type { ChipVariant, IconName } from "@/design";

// ---------------------------------------------------------------------------
// L0..L4 readiness levels
// ---------------------------------------------------------------------------

/**
 * Canonical readiness ladder. Per
 * `PRODUCT_LIFECYCLE_SURFACE_EXPANSION.md` and PHASE-10 the viewer
 * surfaces five named levels. Aliases ("inferred", `*-ready`,
 * "operation-ready") all collapse into one of these slots.
 */
export type ReadinessLevel = "L0" | "L1" | "L2" | "L3" | "L4";

export interface ReadinessLevelAppearance {
  readonly level: ReadinessLevel;
  readonly icon: IconName;
  readonly variant: ChipVariant;
  /** Stable human label; never returned blank. */
  readonly label: string;
}

/**
 * Map an arbitrary `currentReadinessLevel` string (whatever the
 * report writer emits) onto the canonical L0..L4 ladder + a chip
 * variant + the non-color icon glyph that satisfies
 * `DESIGN_POLICY.md` §5.
 *
 * The icon set is restricted to `ICON_REGISTRY` entries
 * (`help`, `pending`, `fact_check`, `verified`, `check_circle`); we
 * do **not** introduce new glyphs (anti-pattern A9).
 */
export function readinessLevelAppearance(
  raw: string | null | undefined,
): ReadinessLevelAppearance {
  const normalized = (raw ?? "").trim().toLowerCase();
  if (
    normalized === "simulation-ready" ||
    normalized === "operation-ready" ||
    normalized === "l4"
  ) {
    return {
      level: "L4",
      icon: "check_circle",
      variant: "success",
      label: raw && raw.trim() ? raw : "simulation-ready",
    };
  }
  if (normalized === "asset-ready" || normalized === "l3") {
    return {
      level: "L3",
      icon: "verified",
      variant: "success",
      label: raw && raw.trim() ? raw : "asset-ready",
    };
  }
  if (normalized === "query-ready" || normalized === "l2") {
    return {
      level: "L2",
      icon: "fact_check",
      variant: "info",
      label: raw && raw.trim() ? raw : "query-ready",
    };
  }
  if (normalized === "visualization-ready" || normalized === "l1") {
    return {
      level: "L1",
      icon: "pending",
      variant: "info",
      label: raw && raw.trim() ? raw : "visualization-ready",
    };
  }
  return {
    level: "L0",
    icon: "help",
    variant: "neutral",
    label: raw && raw.trim() ? raw : "unknown",
  };
}

// ---------------------------------------------------------------------------
// Domain taxonomy (visualization / asset / simulation / operation)
// ---------------------------------------------------------------------------

export type ReadinessDomain =
  | "visualization"
  | "asset"
  | "simulation"
  | "operation";

export interface ReadinessDomainAppearance {
  readonly domain: ReadinessDomain;
  readonly icon: IconName;
  readonly label: string;
}

/**
 * Stable presentation triple for a domain group. Icons are picked
 * from `ICON_REGISTRY` and match the playbook semantics
 * (`visibility` / `category` / `psychology` /
 * `precision_manufacturing`). They are intentionally orthogonal to
 * the L0..L4 icons so a reader sees domain ↔ readiness as two
 * independent axes.
 */
export function readinessDomainAppearance(
  domain: ReadinessDomain,
): ReadinessDomainAppearance {
  switch (domain) {
    case "visualization":
      return { domain, icon: "visibility", label: "Visualization" };
    case "asset":
      return { domain, icon: "category", label: "Asset" };
    case "simulation":
      return { domain, icon: "psychology", label: "Simulation" };
    case "operation":
      return { domain, icon: "precision_manufacturing", label: "Operation" };
    default: {
      // Exhaustiveness guard — `domain` is `never` here.
      const exhaustive: never = domain;
      return {
        domain: exhaustive,
        icon: "help",
        label: "Unknown domain",
      };
    }
  }
}

/**
 * Resolve a readiness checklist item (or a missing-field row) to one
 * of the four canonical lifecycle domains. We inspect (in order):
 *
 *  1. An explicit `domain` field on the item.
 *  2. The item's `key`/`label` string (case-insensitive substring).
 *  3. Heuristics on the requirement name (`connectivity`,
 *     `topology`, `units` → simulation; `equipment`, `asset` →
 *     asset; etc.).
 *
 * Pure function — easy to unit-test and reused by both surfaces.
 */
export function inferGapDomain(
  item: ReadonlyGapLike,
): ReadinessDomain {
  const explicit =
    typeof item.domain === "string" ? item.domain.toLowerCase() : "";
  if (
    explicit === "visualization" ||
    explicit === "asset" ||
    explicit === "simulation" ||
    explicit === "operation"
  ) {
    return explicit;
  }
  const haystack = [
    item.key,
    item.label,
    item.requirement,
    item.message,
  ]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toLowerCase();
  if (
    /\b(operation|maintenance|workorder|sla|inspection)\b/.test(haystack)
  ) {
    return "operation";
  }
  if (
    /\b(simulation|solver|connectivity|topology|units|frame|port|boundary)\b/.test(
      haystack,
    )
  ) {
    return "simulation";
  }
  if (
    /\b(asset|equipment|line|valve|instrument|tag|component|catalog)\b/.test(
      haystack,
    )
  ) {
    return "asset";
  }
  if (
    /\b(visualization|render|style|texture|lod|symbology|legend)\b/.test(
      haystack,
    )
  ) {
    return "visualization";
  }
  // Sensible default: anything unclassified is shown under
  // "simulation" because that is the readiness ladder's terminal
  // domain and most gap copy implies a simulation prerequisite.
  return "simulation";
}

// ---------------------------------------------------------------------------
// Gap shape and severity
// ---------------------------------------------------------------------------

/** Minimal shape we accept when grouping gaps. */
export interface ReadonlyGapLike {
  readonly key?: string;
  readonly label?: string;
  readonly requirement?: string;
  readonly message?: string;
  readonly severity?: string;
  readonly id?: string;
  readonly domain?: string;
  readonly candidateEvidence?: readonly Record<string, unknown>[];
  readonly trustedEvidence?: readonly Record<string, unknown>[];
}

export type ReadinessGapSeverity = "blocking" | "warning" | "info";

export interface ReadinessGapSeverityAppearance {
  readonly severity: ReadinessGapSeverity;
  readonly icon: IconName;
  readonly variant: ChipVariant;
}

/**
 * Resolve the severity of a gap row to its chip / icon presentation.
 * Defaults to `warning` (the most common case for candidate-improvement
 * suggestions). `blocking` is reserved for `report.blockingIssues[]`
 * and explicitly-tagged `severity === 'blocking'`.
 */
export function readinessGapSeverityAppearance(
  severity: string | null | undefined,
): ReadinessGapSeverityAppearance {
  const normalized = (severity ?? "").trim().toLowerCase();
  if (
    normalized === "blocking" ||
    normalized === "error" ||
    normalized === "critical"
  ) {
    return { severity: "blocking", icon: "error", variant: "error" };
  }
  if (normalized === "info") {
    return { severity: "info", icon: "info", variant: "info" };
  }
  return { severity: "warning", icon: "warning", variant: "warning" };
}

// ---------------------------------------------------------------------------
// Grouped gap projection
// ---------------------------------------------------------------------------

export interface ReadinessGapRow {
  readonly id: string;
  /** Short title (`label` or fallback). */
  readonly title: string;
  /** Stable identifier (mono in UI). */
  readonly identifier: string;
  /** Short description. */
  readonly description: string;
  readonly severity: ReadinessGapSeverity;
  readonly domain: ReadinessDomain;
  /** First candidate evidence record (for the Open evidence pivot). */
  readonly firstCandidateEvidence: Record<string, unknown> | null;
  /** First trusted evidence record. */
  readonly firstTrustedEvidence: Record<string, unknown> | null;
  /** Bag of raw evidence rows kept verbatim. */
  readonly rawEvidence: readonly Record<string, unknown>[];
}

export interface ReadinessGapGroup {
  readonly domain: ReadinessDomain;
  readonly appearance: ReadinessDomainAppearance;
  readonly rows: readonly ReadinessGapRow[];
}

/**
 * Project a `SimulationReadinessReport` into a per-domain grouped
 * list of gap rows. Combines `missingRequiredFields`,
 * `blockingIssues`, `warnings`, and the `domainChecklist` rows that
 * are NOT satisfied so a single Card section can render them.
 */
export function groupReadinessGaps(
  report: SimulationReadinessReport | null,
): readonly ReadinessGapGroup[] {
  if (!report) return [];
  const rows: ReadinessGapRow[] = [];

  for (const item of report.missingRequiredFields ?? []) {
    rows.push(toGapRow(item, "blocking"));
  }
  for (const item of report.blockingIssues ?? []) {
    rows.push(toGapRow(item, "blocking"));
  }
  for (const item of report.warnings ?? []) {
    rows.push(toGapRow(item, "warning"));
  }
  for (const item of report.domainChecklist ?? []) {
    if (item.status === "satisfied") continue;
    rows.push(toChecklistGapRow(item));
  }
  for (const item of report.candidateFactsThatCouldImproveReadiness ?? []) {
    rows.push(toGapRow(item, "warning"));
  }

  const byDomain = new Map<ReadinessDomain, ReadinessGapRow[]>();
  for (const row of rows) {
    const bucket = byDomain.get(row.domain) ?? [];
    bucket.push(row);
    byDomain.set(row.domain, bucket);
  }
  const order: readonly ReadinessDomain[] = [
    "visualization",
    "asset",
    "simulation",
    "operation",
  ];
  return order
    .filter((domain) => byDomain.has(domain))
    .map((domain) => ({
      domain,
      appearance: readinessDomainAppearance(domain),
      rows: byDomain.get(domain) ?? [],
    }));
}

function toGapRow(
  item: ReadonlyGapLike,
  fallbackSeverity: ReadinessGapSeverity,
): ReadinessGapRow {
  const severityAppearance = readinessGapSeverityAppearance(
    item.severity ?? fallbackSeverity,
  );
  const domain = inferGapDomain(item);
  const identifier =
    item.key ??
    item.id ??
    item.requirement ??
    item.label ??
    "readiness-gap";
  const title = item.label ?? item.requirement ?? identifier;
  const description =
    item.message ?? "Trusted evidence is required to satisfy this requirement.";
  const candidateEvidence =
    Array.isArray(item.candidateEvidence) && item.candidateEvidence.length > 0
      ? (item.candidateEvidence[0] as Record<string, unknown>)
      : null;
  const trustedEvidence =
    Array.isArray(item.trustedEvidence) && item.trustedEvidence.length > 0
      ? (item.trustedEvidence[0] as Record<string, unknown>)
      : null;
  const rawEvidence: Record<string, unknown>[] = [];
  if (Array.isArray(item.candidateEvidence)) {
    for (const candidate of item.candidateEvidence) {
      if (candidate && typeof candidate === "object") {
        rawEvidence.push(candidate as Record<string, unknown>);
      }
    }
  }
  if (Array.isArray(item.trustedEvidence)) {
    for (const trusted of item.trustedEvidence) {
      if (trusted && typeof trusted === "object") {
        rawEvidence.push(trusted as Record<string, unknown>);
      }
    }
  }
  return {
    id: identifier,
    identifier,
    title,
    description,
    severity: severityAppearance.severity,
    domain,
    firstCandidateEvidence: candidateEvidence,
    firstTrustedEvidence: trustedEvidence,
    rawEvidence,
  };
}

function toChecklistGapRow(
  item: SimulationReadinessChecklistItem,
): ReadinessGapRow {
  const severity: ReadinessGapSeverity =
    item.status === "missing" ? "blocking" : "warning";
  const description =
    item.status === "missing"
      ? "Trusted evidence is required to satisfy this requirement."
      : "Candidate evidence is pending approval before this requirement is satisfied.";
  const candidateRaw = (item.candidateEvidence ?? []) as readonly Record<
    string,
    unknown
  >[];
  const trustedRaw = (item.trustedEvidence ?? []) as readonly Record<
    string,
    unknown
  >[];
  return {
    id: item.key,
    identifier: item.key,
    title: item.label,
    description,
    severity,
    domain: inferGapDomain(item),
    firstCandidateEvidence: candidateRaw[0] ?? null,
    firstTrustedEvidence: trustedRaw[0] ?? null,
    rawEvidence: [...candidateRaw, ...trustedRaw],
  };
}

// ---------------------------------------------------------------------------
// Trusted vs candidate split
// ---------------------------------------------------------------------------

export interface ReadinessFactCounts {
  readonly trusted: number;
  readonly candidate: number;
}

export function countReadinessFacts(
  report: SimulationReadinessReport | null,
): ReadinessFactCounts {
  if (!report) return { trusted: 0, candidate: 0 };
  const trusted = Array.isArray(report.trustedFactsUsed)
    ? report.trustedFactsUsed.length
    : 0;
  const candidate = Array.isArray(report.candidateFactsThatCouldImproveReadiness)
    ? report.candidateFactsThatCouldImproveReadiness.length
    : 0;
  return { trusted, candidate };
}

// ---------------------------------------------------------------------------
// Evidence node resolution
// ---------------------------------------------------------------------------

/**
 * Find the first `UnifiedSceneTreeNode` an evidence record points at.
 * Mirrors the existing `firstEvidenceNode` helper that was private to
 * `TwinProductUx.tsx` so both surfaces share the lookup.
 */
export function firstEvidenceNode(
  evidence: readonly Record<string, unknown>[],
  nodesById: ReadonlyMap<string, UnifiedSceneTreeNode>,
): UnifiedSceneTreeNode | null {
  for (const item of evidence) {
    const nodeId =
      typeof item.nodeId === "string"
        ? item.nodeId
        : typeof item.subjectNodeId === "string"
          ? item.subjectNodeId
          : undefined;
    if (nodeId && nodesById.has(nodeId)) return nodesById.get(nodeId) ?? null;
    const refs = Array.isArray(item.evidenceRefs) ? item.evidenceRefs : [];
    for (const ref of refs) {
      if (
        typeof ref === "object" &&
        ref !== null &&
        "id" in ref &&
        typeof (ref as { id?: unknown }).id === "string"
      ) {
        const node = nodesById.get((ref as { id: string }).id);
        if (node) return node;
      }
    }
  }
  return null;
}

/**
 * Resolve the index-node id that an evidence record points at,
 * without requiring the caller to maintain a `nodesById` map. Used
 * by the ToolCard "Open evidence" pivot which builds a synthetic
 * `SceneSelection` for the scene-tree dispatch even when the
 * evidence node has not yet been hydrated.
 */
export function firstEvidenceNodeId(
  evidence: readonly Record<string, unknown>[],
): string | null {
  for (const item of evidence) {
    if (typeof item.nodeId === "string") return item.nodeId;
    if (typeof item.subjectNodeId === "string") return item.subjectNodeId;
    const refs = Array.isArray(item.evidenceRefs) ? item.evidenceRefs : [];
    for (const ref of refs) {
      if (
        typeof ref === "object" &&
        ref !== null &&
        "id" in ref &&
        typeof (ref as { id?: unknown }).id === "string"
      ) {
        return (ref as { id: string }).id;
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Export SDK seam
// ---------------------------------------------------------------------------

/**
 * Minimum shape we accept for the (future) platform SDK readiness
 * export endpoint. The component imports this so it can feature-detect
 * the method **without** importing any concrete `GTwinClient` symbol —
 * the export action stays disabled until the platform SDK ships the
 * method (per WP-10 blocker rules).
 *
 *   client?.scene?.exportReadinessGraph?.(layerId)
 */
export interface ReadinessExportCapableClient {
  readonly scene?: {
    readonly exportReadinessGraph?: (
      layerId: string,
    ) => Promise<ReadinessGraphSnapshot> | ReadinessGraphSnapshot;
  };
}

/**
 * Graph snapshot the export endpoint should return. Shape is the
 * minimum the viewer needs to write a downloadable JSON; the SDK is
 * free to extend it.
 */
export interface ReadinessGraphSnapshot {
  readonly schema?: string;
  readonly sceneId?: string;
  readonly runId?: string;
  readonly exportedAt?: string;
  readonly report?: SimulationReadinessReport | null;
  readonly nodes?: readonly Record<string, unknown>[];
  readonly edges?: readonly Record<string, unknown>[];
}

/**
 * Feature-detect `client.scene.exportReadinessGraph`. Pure helper so
 * tests and the component itself agree on the predicate.
 */
export function hasExportReadinessGraph(
  client: unknown,
): client is Required<ReadinessExportCapableClient> & {
  readonly scene: {
    readonly exportReadinessGraph: (
      layerId: string,
    ) => Promise<ReadinessGraphSnapshot> | ReadinessGraphSnapshot;
  };
} {
  if (!client || typeof client !== "object") return false;
  const scene = (client as { scene?: unknown }).scene;
  if (!scene || typeof scene !== "object") return false;
  const fn = (scene as { exportReadinessGraph?: unknown })
    .exportReadinessGraph;
  return typeof fn === "function";
}
